import _ from 'lodash'
import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'

import {
  useAddConsultationBiologicalFileMutation,
  useCreateConsultationBiologicalMutation,
  useDeleteConsultationBiologicalFilesMutation,
  useDeleteConsultationBiologicalMutation,
} from '@/core/services/consultation'
import { useExtractVitalSignsMutation } from '@/core/services/medconnect-agent-service'
import { useAppSelector, useAppDispatch } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { selectConsultationById } from '@/redux/reducers/consultation.reducer'
import { getConsultation } from '@/redux/reducers/consultation.reducer'

import { getApiErrorMessage } from '@/utils/errors'

import Biologicals from '../components/Biologicals'

type BiologicalContainerProps = {
  consultationId?: string
}

export function BiologicalsContainer(props: BiologicalContainerProps) {
  const { consultationId } = props
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const dispatch = useAppDispatch()

  const authUser = useAppSelector(selectAuthUser)
  const consultation = useAppSelector((state) => (consultationId ? selectConsultationById(state, consultationId) : undefined))

  const [addBiologicalFile] = useAddConsultationBiologicalFileMutation()
  const [createBiological, { isLoading: isCreatingBiological }] = useCreateConsultationBiologicalMutation()
  const [deleteBiological, { isLoading: isDeletingBiological }] = useDeleteConsultationBiologicalMutation()
  const [deleteBiologicalFiles, { isLoading: isDeletingBiologicalFiles }] = useDeleteConsultationBiologicalFilesMutation()

  // Vital signs extraction integration
  const [extractVitalSigns, { isLoading: isProcessingVitalSigns }] = useExtractVitalSignsMutation()
  const [processingVitalSignsFileKey, setProcessingVitalSignsFileKey] = useState<string | null>(null)

  const biologicals = useMemo(() => {
    if (!consultation) return []
    return _.map(consultation.biologicals, (biological) => ({
      ...biological,
      files: consultation.files.filter((file) => biological.files.includes(file.key) || file.key === biological.file),
    }))
  }, [consultation])

  if (!consultationId) return null

  return (
    <Biologicals
      biologicals={biologicals}
      isCreatingBiological={isCreatingBiological}
      isDeletingBiological={isDeletingBiological}
      isDeletingBiologicalFiles={isDeletingBiologicalFiles}
      onCreateBiological={async (values) => {
        try {
          await createBiological({ consultationId, biological: values }).unwrap()
        } catch (error) {
          enqueueSnackbar(getApiErrorMessage(error, t('notification.consultation.biological.create.error')), { variant: 'error' })
        }
      }}
      onUploadBiologicalFile={async (file, biologicalId) => {
        try {
          await addBiologicalFile({ biologicalId, consultationId, file }).unwrap()
          enqueueSnackbar(t('notification.consultation.biological.create.success'), { variant: 'success' })
        } catch (error) {
          enqueueSnackbar(getApiErrorMessage(error, t('notification.consultation.biologicalFile.upload.error')), { variant: 'error' })
        }
      }}
      onDeleteBiological={async (biologicalId) => {
        try {
          await deleteBiological({ biologicalId, consultationId }).unwrap()
          enqueueSnackbar(t('notification.consultation.biological.delete.success'), { variant: 'success' })
        } catch (error) {
          enqueueSnackbar(getApiErrorMessage(error, t('notification.consultation.biological.delete.error')), { variant: 'error' })
        }
      }}
      onDeleteBiologicalFiles={async (biologicalId, files) => {
        try {
          await deleteBiologicalFiles({ biologicalId, consultationId, files })
        } catch (error) {
          enqueueSnackbar(getApiErrorMessage(error, t('notification.consultation.biologicalFile.delete.error')), { variant: 'error' })
        }
      }}
      onExtractVitalSigns={async (fileKey: string) => {
        if (!consultationId) return

        setProcessingVitalSignsFileKey(fileKey)

        try {
          const result = await extractVitalSigns({
            fileKey,
            consultationId,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          }).unwrap()

          // Check if operation was successful
          if (result.success) {
            // Check if vital signs were actually updated
            if (result.vitalSignsUpdated !== undefined && result.vitalSignsUpdated > 0) {
              enqueueSnackbar(
                t('notification.document.vitalSigns.success', {
                  count: result.vitalSignsUpdated,
                  defaultValue: `Successfully extracted ${result.vitalSignsUpdated} vital sign(s)`,
                }),
                { variant: 'success' }
              )
              // Refresh consultation to show updated vital signs
              if (consultationId) {
                dispatch(getConsultation(consultationId))
              }
            } else if (result.vitalSignsUpdated === 0) {
              // Document processed but no vital signs found
              enqueueSnackbar(
                result.message ||
                  t('notification.document.vitalSigns.noData', {
                    defaultValue: 'No vital signs data found in the document. The document was processed, but no vital signs were detected.',
                  }),
                { variant: 'warning' }
              )
            } else {
              // Success but vitalSignsUpdated is undefined - treat result.success === true as success
              if (result.success === true) {
                enqueueSnackbar(
                  result.message ||
                    t('notification.document.vitalSigns.success', {
                      count: 1,
                      defaultValue: 'Vital signs extraction completed',
                    }),
                  { variant: 'success' }
                )
                // Refresh consultation to show updated vital signs
                if (consultationId) {
                  dispatch(getConsultation(consultationId))
                }
              } else {
                // Unclear result - show generic success message
                enqueueSnackbar(
                  result.message ||
                    t('notification.document.vitalSigns.success', {
                      count: 1,
                      defaultValue: 'Document processed',
                    }),
                  { variant: 'info' }
                )
              }
            }
          } else {
            // Operation failed
            enqueueSnackbar(result.error || result.message || t('notification.document.vitalSigns.error', { defaultValue: 'Failed to extract vital signs' }), {
              variant: 'error',
            })
          }
        } catch (e: unknown) {
          enqueueSnackbar(getApiErrorMessage(e, t('notification.document.vitalSigns.error', { defaultValue: 'Failed to extract vital signs' })), {
            variant: 'error',
          })
        } finally {
          setProcessingVitalSignsFileKey(null)
        }
      }}
      isProcessingVitalSigns={isProcessingVitalSigns}
      processingVitalSignsFileKey={processingVitalSignsFileKey}
      consultationId={consultationId}
      readOnly={false}
    />
  )
}

export default BiologicalsContainer
